import { useQuery } from '@tanstack/react-query';
import { ApiError, get } from '@/shared/api/api-client';

/**
 * Canonical React Query key for a practice's exam rooms as a device-room picker.
 */
export function examRoomsQueryKey(
  practiceId: string | null | undefined,
): readonly [string, string, string | null | undefined] {
  return ['settings', 'exam-rooms', practiceId];
}

/** One exam room as the device form's Room picker needs it. */
export interface ExamRoomOption {
  /** `dbo.PracticeExamRooms.Id` — the value `POST|PATCH /api/Resource` takes as `roomId`. */
  id: string;
  name: string;
}

/**
 * Wire row of `GET /api/Practice/RoomList/{practiceId}` (`ExamRoomListResponse`,
 * PracticeSetupService.GetExamRoomByPracticeId): `{ id, examRoom }`, already
 * filtered to `IsActive == true` and ordered by name server-side. `roomName` /
 * `name` are accepted as well so a renamed projection does not silently empty
 * the picker.
 */
interface ExamRoomListRow {
  id?: string | null;
  examRoom?: string | null;
  roomName?: string | null;
  name?: string | null;
}

/**
 * Pure: the wire array → picker options. Rows without an id or a name are
 * dropped rather than rendered as blank options.
 */
export function normaliseExamRooms(raw: unknown): ExamRoomOption[] {
  if (!Array.isArray(raw)) return [];
  const out: ExamRoomOption[] = [];
  for (const row of raw as ExamRoomListRow[]) {
    if (!row || typeof row !== 'object') continue;
    const id = row.id ? String(row.id) : '';
    const name = (row.examRoom ?? row.roomName ?? row.name ?? '').toString().trim();
    if (!id || !name) continue;
    out.push({ id, name });
  }
  return out;
}

/**
 * Active exam rooms of a practice, for allocating a device to a room —
 * PROD-2008 (epic PROD-2007; ruling 2026-09-11: rooms are places, a device is
 * optionally allocated to one).
 *
 *   GET /api/Practice/RoomList/{practiceId}
 *
 * The controller answers `NotFound()` (404 — despite its 204 annotation) when
 * the practice has no exam rooms; that is "no rooms", not a failure, so it
 * resolves to `[]` and the picker shows only "No room (mobile)". An empty body
 * is treated the same way.
 *
 * Distinct from `useExamRoomList` (`EditPracticeExamRoom`, the WH / profile
 * editors): that endpoint returns the block shape keyed by `examHourId`, this
 * one returns the plain room rows keyed by the id `roomId` validates against.
 */
export function useExamRooms(practiceId: string | null | undefined) {
  return useQuery<ExamRoomOption[]>({
    queryKey: examRoomsQueryKey(practiceId),
    queryFn: async ({ signal }) => {
      if (!practiceId) {
        throw new Error('useExamRooms called without practiceId');
      }
      try {
        const res = await get<unknown>(
          `/api/Practice/RoomList/${encodeURIComponent(practiceId)}`,
          { signal },
        );
        return normaliseExamRooms(res);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return [];
        throw err;
      }
    },
    enabled: !!practiceId,
    staleTime: 5 * 60_000,
    // A failed picker load is shown as the Room field's helper text
    // ("Rooms unavailable right now…"); the global "Something went wrong"
    // toast must not fire on top of it.
    meta: { skipGlobalError: true },
  });
}
